import React, { useState, useEffect } from 'react';
import { FaSearch, FaUserPlus, FaUserFriends, FaCheck, FaTimes, FaTrash, FaClock, FaArrowLeft, FaTrophy } from 'react-icons/fa';
import { apiService } from '../services/apiService';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

interface Friend {
  id: number;
  username: string;
  email: string;
  isOnline: boolean;
}

interface FriendRequest {
  requestId: number;
  userId: number;
  username: string;
  email: string;
  createdAt: string;
}

interface UserSearchResult {
  id: number;
  username: string;
  email: string;
}

const Friends: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'friends' | 'requests' | 'search'>('friends');
  const [friends, setFriends] = useState<Friend[]>([]);
  const [friendRequests, setFriendRequests] = useState<FriendRequest[]>([]);
  const [searchResults, setSearchResults] = useState<UserSearchResult[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  

  useEffect(() => {
    fetchFriends();
    fetchFriendRequests();
  }, []);

  const fetchFriends = async () => {
    try {
      const response = await apiService.get('/api/friends/list');
      if (response.success) {
        setFriends(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching friends:', error);
      toast.error('Failed to load friends');
    }
  };

  const fetchFriendRequests = async () => {
    try {
      const response = await apiService.get('/api/friends/requests');
      if (response.success) {
        setFriendRequests(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching friend requests:', error);
    }
  };

  const searchUsers = async () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    try {
      const response = await apiService.get(`/api/friends/search?query=${encodeURIComponent(searchQuery)}`);
      if (response.success) {
        setSearchResults(response.data || []);
      }
    } catch (error) {
      console.error('Error searching users:', error);
      toast.error('Failed to search users');
    } finally {
      setSearchLoading(false);
    }
  };

  const sendFriendRequest = async (usernameOrEmail: string) => {
    setLoading(true);
    try {
      const response = await apiService.post('/api/friends/request', {
        usernameOrEmail
      });
      if (response.success) {
        toast.success('Friend request sent!');
        setSearchQuery('');
        setSearchResults([]);
      } else {
        toast.error(response.message || 'Failed to send friend request');
      }
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Failed to send friend request';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const acceptFriendRequest = async (requestId: number) => {
    setLoading(true);
    try {
      const response = await apiService.post(`/api/friends/accept/${requestId}`, {});
      if (response.success) {
        toast.success('Friend request accepted!');
        fetchFriends();
        fetchFriendRequests();
      } else {
        toast.error(response.message || 'Failed to accept friend request');
      }
    } catch (error) {
      toast.error('Failed to accept friend request');
    } finally {
      setLoading(false);
    }
  };

  const rejectFriendRequest = async (requestId: number) => {
    setLoading(true);
    try {
      const response = await apiService.delete(`/api/friends/reject/${requestId}`);
      if (response.success) {
        toast.info('Friend request rejected');
        fetchFriendRequests();
      } else {
        toast.error(response.message || 'Failed to reject friend request');
      }
    } catch (error) {
      toast.error('Failed to reject friend request');
    } finally {
      setLoading(false);
    }
  };

  const removeFriend = async (friendId: number) => {
    if (!window.confirm('Are you sure you want to remove this friend?')) return;

    setLoading(true);
    try {
      const response = await apiService.delete(`/api/friends/remove/${friendId}`);
      if (response.success) {
        toast.success('Friend removed');
        fetchFriends();
      } else {
        toast.error(response.message || 'Failed to remove friend');
      }
    } catch (error) {
      toast.error('Failed to remove friend');
    } finally {
      setLoading(false);
    }
  };

  const handleChallengeFriend = (friend: Friend) => {
    // Navigate to games page - user can select game and challenge from there
    navigate('/p2p-games', { state: { selectedFriendId: friend.id, selectedFriendUsername: friend.username } });
  };

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: 24 }}>
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <button
          onClick={() => window.history.back()}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 16px',
            background: 'none',
            border: '1px solid #e2e8f0',
            borderRadius: 8,
            color: '#4a5568',
            cursor: 'pointer',
            marginBottom: 16,
            fontSize: 14
          }}
        >
          <FaArrowLeft /> Back
        </button>
        <h1 style={{ 
          fontSize: isMobile ? 24 : 32, 
          fontWeight: 700, 
          color: '#1a202c', 
          marginBottom: 8,
          display: 'flex',
          alignItems: 'center',
          gap: 12
        }}>
          <FaUserFriends style={{ color: '#667eea' }} />
          Friends
        </h1>
        <p style={{ fontSize: isMobile ? 14 : 16, color: '#718096' }}>
          Manage your friends to challenge them in P2P games
        </p>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: 16,
        borderBottom: '2px solid #e2e8f0',
        marginBottom: 24
      }}>
        <button
          onClick={() => setActiveTab('friends')}
          style={{
            padding: isMobile ? '10px 12px' : '12px 24px',
            background: 'none',
            border: 'none',
            borderBottom: `3px solid ${activeTab === 'friends' ? '#667eea' : 'transparent'}`,
            color: activeTab === 'friends' ? '#667eea' : '#718096',
            fontWeight: 600,
            fontSize: isMobile ? 13 : 15,
            cursor: 'pointer',
            marginBottom: -2,
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? 4 : 8,
            whiteSpace: 'nowrap',
            flexShrink: 0
          }}
        >
          <FaUserFriends /> {isMobile ? 'Friends' : 'My Friends'} ({friends.length})
        </button>
        <button
          onClick={() => setActiveTab('requests')}
          style={{
            padding: isMobile ? '10px 12px' : '12px 24px',
            background: 'none',
            border: 'none',
            borderBottom: `3px solid ${activeTab === 'requests' ? '#667eea' : 'transparent'}`,
            color: activeTab === 'requests' ? '#667eea' : '#718096',
            fontWeight: 600,
            fontSize: isMobile ? 13 : 15,
            cursor: 'pointer',
            marginBottom: -2,
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? 4 : 8,
            position: 'relative',
            whiteSpace: 'nowrap',
            flexShrink: 0
          }}
        >
          <FaClock /> {isMobile ? 'Requests' : 'Requests'} ({friendRequests.length})
          {friendRequests.length > 0 && (
            <span style={{
              position: 'absolute',
              top: 4,
              right: 4,
              width: 8,
              height: 8,
              background: '#ef4444',
              borderRadius: '50%'
            }} />
          )}
        </button>
        <button
          onClick={() => setActiveTab('search')}
          style={{
            padding: isMobile ? '10px 12px' : '12px 24px',
            background: 'none',
            border: 'none',
            borderBottom: `3px solid ${activeTab === 'search' ? '#667eea' : 'transparent'}`,
            color: activeTab === 'search' ? '#667eea' : '#718096',
            fontWeight: 600,
            fontSize: isMobile ? 13 : 15,
            cursor: 'pointer',
            marginBottom: -2,
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? 4 : 8,
            whiteSpace: 'nowrap',
            flexShrink: 0
          }}
        >
          <FaUserPlus /> {isMobile ? 'Add' : 'Add Friends'}
        </button>
      </div>

      {/* Content */}
      {activeTab === 'friends' && (
        <div>
          {friends.length === 0 ? (
            <div style={{
              padding: 60,
              textAlign: 'center',
              background: '#f7fafc',
              borderRadius: 12,
              border: '2px dashed #cbd5e0'
            }}>
              <FaUserFriends style={{ fontSize: 48, color: '#cbd5e0', marginBottom: 16 }} />
              <p style={{ fontSize: 18, fontWeight: 600, color: '#4a5568', marginBottom: 8 }}>
                No friends yet
              </p>
              <p style={{ fontSize: 14, color: '#718096', marginBottom: 16 }}>
                Add friends to challenge them in P2P games
              </p>
              <button
                onClick={() => setActiveTab('search')}
                style={{
                  padding: '10px 20px',
                  background: '#667eea',
                  color: 'white',
                  border: 'none',
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Add Friends
              </button>
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              {!isMobile ? (
                <div style={{
                  background: 'white',
                  borderRadius: 12,
                  border: '1px solid #e2e8f0',
                  overflow: 'hidden'
                }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#f7fafc', borderBottom: '2px solid #e2e8f0' }}>
                        <th style={{ padding: '16px', textAlign: 'left', fontSize: 14, fontWeight: 600, color: '#4a5568' }}>Friend</th>
                        <th style={{ padding: '16px', textAlign: 'left', fontSize: 14, fontWeight: 600, color: '#4a5568' }}>Email</th>
                        <th style={{ padding: '16px', textAlign: 'left', fontSize: 14, fontWeight: 600, color: '#4a5568' }}>Status</th>
                        <th style={{ padding: '16px', textAlign: 'center', fontSize: 14, fontWeight: 600, color: '#4a5568' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {friends.map((friend, index) => (
                        <tr 
                          key={friend.id}
                          style={{ 
                            borderBottom: index < friends.length - 1 ? '1px solid #e2e8f0' : 'none',
                            transition: 'background 0.2s'
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = '#f7fafc'}
                          onMouseLeave={e => e.currentTarget.style.background = 'white'}
                        >
                          <td style={{ padding: '16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                              <div style={{
                                width: 40,
                                height: 40,
                                borderRadius: '50%',
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontSize: 16,
                                fontWeight: 700,
                                flexShrink: 0
                              }}>
                                {friend.username.charAt(0).toUpperCase()}
                              </div>
                              <div style={{ fontSize: 15, fontWeight: 600, color: '#1a202c' }}>
                                {friend.username}
                              </div>
                            </div>
                          </td>
                          <td style={{ padding: '16px', fontSize: 14, color: '#718096' }}>
                            {friend.email}
                          </td>
                          <td style={{ padding: '16px' }}>
                            <span style={{
                              padding: '4px 12px',
                              background: friend.isOnline ? '#c6f6d5' : '#e2e8f0',
                              color: friend.isOnline ? '#22543d' : '#718096',
                              borderRadius: 12,
                              fontSize: 12,
                              fontWeight: 600
                            }}>
                              {friend.isOnline ? 'Online' : 'Offline'}
                            </span>
                          </td>
                          <td style={{ padding: '16px', textAlign: 'center' }}>
                            <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                              <button
                                onClick={() => handleChallengeFriend(friend)}
                                style={{
                                  padding: '8px 16px',
                                  background: '#667eea',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: 8,
                                  fontSize: 13,
                                  fontWeight: 600,
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 6,
                                  transition: 'all 0.2s'
                                }}
                                onMouseEnter={e => e.currentTarget.style.background = '#5568d3'}
                                onMouseLeave={e => e.currentTarget.style.background = '#667eea'}
                              >
                                <FaTrophy /> Challenge
                              </button>
                              <button
                                onClick={() => removeFriend(friend.id)}
                                disabled={loading}
                                style={{
                                  padding: '8px 16px',
                                  background: '#fed7d7',
                                  color: '#c53030',
                                  border: 'none',
                                  borderRadius: 8,
                                  fontSize: 13,
                                  fontWeight: 600,
                                  cursor: loading ? 'not-allowed' : 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 6,
                                  opacity: loading ? 0.7 : 1
                                }}
                              >
                                <FaTrash /> Remove
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                /* Mobile Card View */
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {friends.map(friend => (
                    <div
                      key={friend.id}
                      style={{
                        padding: 16,
                        background: 'white',
                        border: '1px solid #e2e8f0',
                        borderRadius: 12,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                        <div style={{
                          width: 48,
                          height: 48,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: 18,
                          fontWeight: 700,
                          flexShrink: 0
                        }}>
                          {friend.username.charAt(0).toUpperCase()}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 16, fontWeight: 600, color: '#1a202c', marginBottom: 4 }}>
                            {friend.username}
                          </div>
                          <div style={{ fontSize: 12, color: '#718096', marginBottom: 4 }}>
                            {friend.email}
                          </div>
                          <span style={{
                            padding: '4px 8px',
                            background: friend.isOnline ? '#c6f6d5' : '#e2e8f0',
                            color: friend.isOnline ? '#22543d' : '#718096',
                            borderRadius: 8,
                            fontSize: 11,
                            fontWeight: 600
                          }}>
                            {friend.isOnline ? 'Online' : 'Offline'}
                          </span>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 8, flexDirection: 'column' }}>
                        <button
                          onClick={() => handleChallengeFriend(friend)}
                          style={{
                            width: '100%',
                            padding: '10px 16px',
                            background: '#667eea',
                            color: 'white',
                            border: 'none',
                            borderRadius: 8,
                            fontSize: 14,
                            fontWeight: 600,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 8
                          }}
                        >
                          <FaTrophy /> Challenge Friend
                        </button>
                        <button
                          onClick={() => removeFriend(friend.id)}
                          disabled={loading}
                          style={{
                            width: '100%',
                            padding: '10px 16px',
                            background: '#fed7d7',
                            color: '#c53030',
                            border: 'none',
                            borderRadius: 8,
                            fontSize: 14,
                            fontWeight: 600,
                            cursor: loading ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 8,
                            opacity: loading ? 0.7 : 1
                          }}
                        >
                          <FaTrash /> Remove Friend
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {activeTab === 'requests' && (
        <div>
          {friendRequests.length === 0 ? (
            <div style={{
              padding: 60,
              textAlign: 'center',
              background: '#f7fafc',
              borderRadius: 12,
              border: '2px dashed #cbd5e0'
            }}>
              <FaClock style={{ fontSize: 48, color: '#cbd5e0', marginBottom: 16 }} />
              <p style={{ fontSize: 18, fontWeight: 600, color: '#4a5568', marginBottom: 8 }}>
                No pending requests
              </p>
              <p style={{ fontSize: 14, color: '#718096' }}>
                Friend requests will appear here
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {friendRequests.map(request => (
                <div
                  key={request.requestId}
                  style={{
                    padding: 20,
                    background: 'white',
                    border: '1px solid #e2e8f0',
                    borderRadius: 12,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: 18,
                      fontWeight: 700
                    }}>
                      {request.username.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 600, color: '#1a202c' }}>
                        {request.username}
                      </div>
                      <div style={{ fontSize: 12, color: '#718096' }}>
                        {request.email}
                      </div>
                      <div style={{ fontSize: 11, color: '#a0aec0', marginTop: 4 }}>
                        {new Date(request.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={() => acceptFriendRequest(request.requestId)}
                      disabled={loading}
                      style={{
                        padding: '8px 16px',
                        background: '#48bb78',
                        color: 'white',
                        border: 'none',
                        borderRadius: 8,
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6
                      }}
                    >
                      <FaCheck /> Accept
                    </button>
                    <button
                      onClick={() => rejectFriendRequest(request.requestId)}
                      disabled={loading}
                      style={{
                        padding: '8px 16px',
                        background: '#e2e8f0',
                        color: '#4a5568',
                        border: 'none',
                        borderRadius: 8,
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6
                      }}
                    >
                      <FaTimes /> Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'search' && (
        <div>
          <div style={{ marginBottom: 24 }}>
            <div style={{ position: 'relative' }}>
              <FaSearch style={{
                position: 'absolute',
                left: 16,
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#a0aec0',
                fontSize: 16
              }} />
              <input
                type="text"
                placeholder="Search by username or email..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onKeyPress={e => e.key === 'Enter' && searchUsers()}
                style={{
                  width: '100%',
                  padding: '14px 16px 14px 48px',
                  border: '2px solid #e2e8f0',
                  borderRadius: 12,
                  fontSize: 15,
                  outline: 'none',
                  transition: 'border-color 0.2s'
                }}
                onFocus={e => e.target.style.borderColor = '#667eea'}
                onBlur={e => e.target.style.borderColor = '#e2e8f0'}
              />
            </div>
            <button
              onClick={searchUsers}
              disabled={searchLoading || !searchQuery.trim()}
              style={{
                marginTop: 12,
                padding: '10px 24px',
                background: '#667eea',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 600,
                cursor: searchQuery.trim() ? 'pointer' : 'not-allowed',
                opacity: searchQuery.trim() ? 1 : 0.5
              }}
            >
              {searchLoading ? 'Searching...' : 'Search'}
            </button>
          </div>

          {searchResults.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {searchResults.map(user => (
                <div
                  key={user.id}
                  style={{
                    padding: 20,
                    background: 'white',
                    border: '1px solid #e2e8f0',
                    borderRadius: 12,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: 18,
                      fontWeight: 700
                    }}>
                      {user.username.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 600, color: '#1a202c' }}>
                        {user.username}
                      </div>
                      <div style={{ fontSize: 12, color: '#718096' }}>
                        {user.email}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => sendFriendRequest(user.username)}
                    disabled={loading}
                    style={{
                      padding: '8px 16px',
                      background: '#667eea',
                      color: 'white',
                      border: 'none',
                      borderRadius: 8,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6
                    }}
                  >
                    <FaUserPlus /> Add Friend
                  </button>
                </div>
              ))}
            </div>
          )}

          {searchQuery && searchResults.length === 0 && !searchLoading && (
            <div style={{
              padding: 40,
              textAlign: 'center',
              background: '#f7fafc',
              borderRadius: 12,
              border: '2px dashed #cbd5e0'
            }}>
              <p style={{ fontSize: 16, color: '#718096' }}>
                No users found matching "{searchQuery}"
              </p>
            </div>
          )}
        </div>
      )}

    </div>
  );
};

export default Friends;

